import React, { useCallback, useEffect } from 'react';
import { Flex } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';
import { useDataLayer } from 'hooks/useDataLayer';
import { useInView } from 'react-intersection-observer';
import { usePersonalisation } from 'hooks/usePersonalisation';
import PropertyCard from 'components/PropertyCard/PropertyCard';
import { PropertyCardProps } from 'types/property';

export interface PromoResultProps extends Omit<PropertyCardProps, 'id'> {
  id: string;
  index: number;
  offerId: string;
  threshold?: number;
  onScrollIntoView?: (index: number) => void;
  promotionName?: string;
}

const PromoResult: React.FC<PromoResultProps> = ({
  id: propertyId,
  index,
  threshold,
  offerId,
  onScrollIntoView,
  promotionName,
  ...rest
}) => {
  const { emitInteractionEvent } = useDataLayer();
  const personalisation = usePersonalisation();
  const [ref, inView] = useInView({ root: null, threshold });

  const handlePropertyLinkOnClick = useCallback(() => {
    const cardNumber = index + 1;
    const customAttributes = { user_event_value: propertyId };
    emitInteractionEvent({ type: 'Promo Area', value: `Card ${cardNumber} Selected`, customAttributes });
    personalisation.trackPromoClick(propertyId);
  }, [personalisation, emitInteractionEvent, index, propertyId]);

  useEffect(() => {
    if (inView && onScrollIntoView) {
      onScrollIntoView(index);
    }
  }, [inView, onScrollIntoView, index]);

  return (
    <Flex ref={ref} pl={[3, 3, 0]} box-shadow={themeGet('shadows.heavy')} border-bottom={themeGet('space.4')}>
      <PropertyCard
        {...rest}
        id={propertyId}
        offerId={offerId}
        featuredOfferId={offerId}
        inline
        onClick={handlePropertyLinkOnClick}
        isPromoCard
        promotionName={promotionName}
      />
    </Flex>
  );
};

export default PromoResult;
